// A1 : liste des installations (M01, T02, charte D-038). Ce qui est à traiter en tête (installations en
// attente de validation, doublons), filtres appliqués côté serveur, tableau à lignes sélectionnables et
// résumé de l'installation choisie à côté ; pagination par curseur.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CATEGORIES_INSTALLATIONS, CODES_PROVINCES, formaterNombre } from '@carburanttrack/shared';
import { api } from '../../api/client';
import type { InstallationListe } from '../../api/types';
import { useFiltres } from '../../app/filtres';
import { aUnRole, useSession } from '../../app/session';
import { MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { montrerSurPetitEcran } from '../../composants/selection';
import { classeEtat, libelleCategorie, libelleConfidentialite, libelleEtat, libelleProvince } from '../../composants/format';
import { ListePointsAttention, usePointsReferentiel, useSituation } from '../accueil/communs';

const SEGMENTS_ETATS: { etat: string; libelle: string }[] = [
  { etat: '', libelle: 'Toutes' },
  { etat: 'en_attente_validation', libelle: 'En attente' },
  { etat: 'valide', libelle: 'Validées' },
  { etat: 'brouillon', libelle: 'Brouillons' },
];

export function ListeInstallations() {
  const [params, setParams] = useSearchParams();
  const { province: provinceGlobale } = useFiltres();
  const { etat } = useSession();
  const situation = useSituation();
  const pointsAttention = usePointsReferentiel(situation.data).filter((p) => p.cle !== 'brouillons');
  const [recherche, setRecherche] = useState(params.get('q') ?? '');
  const [choisie, setChoisie] = useState<string | null>(null);
  const categorie = params.get('categorie') ?? '';
  const province = params.get('province') ?? provinceGlobale ?? '';
  const etatValidation = params.get('etat') ?? '';
  const q = params.get('q') ?? '';

  useEffect(() => setRecherche(q), [q]);
  // Derniers filtres, réutilisables pour enregistrer une vue dans les préférences.
  useEffect(() => {
    try {
      sessionStorage.setItem('ct-derniere-recherche-installations', params.toString());
    } catch {
      // Sans conséquence.
    }
  }, [params]);

  const filtres = new URLSearchParams();
  if (categorie) filtres.set('categorie', categorie);
  if (province) filtres.set('province', province);
  if (etatValidation) filtres.set('etat', etatValidation);
  if (q) filtres.set('q', q);
  filtres.set('limite', '50');

  const requete = useInfiniteQuery({
    queryKey: ['installations', filtres.toString()],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const p = new URLSearchParams(filtres);
      if (pageParam) p.set('curseur', pageParam);
      return api<InstallationListe[]>(`/api/v1/installations?${p.toString()}`);
    },
    getNextPageParam: (derniere) => (derniere.meta.curseurSuivant as string | null) ?? null,
  });

  const changer = (cle: string, valeur: string) => {
    const p = new URLSearchParams(params);
    if (valeur) p.set(cle, valeur);
    else p.delete(cle);
    setParams(p, { replace: true });
  };
  const lignes = requete.data?.pages.flatMap((p) => p.donnees) ?? [];
  const selection = lignes.find((l) => l.id === choisie) ?? lignes[0] ?? null;
  const peutCreer = aUnRole(etat, 'administrateur_national', 'agent_administratif', 'distributeur');
  const administrateur = aUnRole(etat, 'administrateur_national');
  // Total exact du périmètre pour les filtres hors recherche textuelle (comptes du référentiel).
  const total = situation.data?.installationsLisibles
    ? situation.data.comptes
        .filter((c) => (!categorie || c.categorie === categorie) && (!province || c.provinceCode === province) && (!etatValidation || c.etat === etatValidation))
        .reduce((n, c) => n + c.nombre, 0)
    : null;
  const titre = categorie === 'station' && etat?.profil === 'distributeur' ? 'Stations du réseau' : 'Installations';
  const segments = SEGMENTS_ETATS.some((s) => s.etat === etatValidation) ? SEGMENTS_ETATS : [...SEGMENTS_ETATS, { etat: etatValidation, libelle: libelleEtat(etatValidation) }];

  return (
    <div className="page-installations">
      <div className="page-tete">
        <div>
          <h1>{titre}</h1>
          <p>
            {total !== null && !q ? `${formaterNombre(total)} installation${total > 1 ? 's' : ''} correspondant aux filtres, ` : ''}
            {province ? libelleProvince(province) : 'tout votre périmètre'}
            {q ? `, recherche « ${q} »` : ''}
          </p>
        </div>
        {peutCreer && (
          <Link className="bouton primaire" to="/referentiel/installations/nouvelle">
            <Icone nom="ajout" />
            Nouvelle installation
          </Link>
        )}
      </div>

      {pointsAttention.length > 0 && (
        <section className="a-traiter" aria-label="À traiter">
          <ListePointsAttention points={pointsAttention} />
        </section>
      )}

      <form
        className="barre-filtres"
        role="search"
        aria-label="Filtres des installations"
        onSubmit={(e) => {
          e.preventDefault();
          changer('q', recherche.trim());
        }}
      >
        <div className="champ large">
          <label htmlFor="f-q">Nom</label>
          <input id="f-q" type="search" placeholder="Par exemple : Owendo, Azingo" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
        </div>
        <div className="champ">
          <label htmlFor="f-categorie">Catégorie</label>
          <select id="f-categorie" value={categorie} onChange={(e) => changer('categorie', e.target.value)}>
            <option value="">Toutes</option>
            {CATEGORIES_INSTALLATIONS.map((c) => (
              <option key={c} value={c}>
                {libelleCategorie(c)}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="f-province">Province</label>
          <select id="f-province" value={province} onChange={(e) => changer('province', e.target.value)}>
            <option value="">Toutes</option>
            {CODES_PROVINCES.map((c) => (
              <option key={c} value={c}>
                {libelleProvince(c)}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="bouton">
          Rechercher
        </button>
        <div className="segments" role="radiogroup" aria-label="État de validation">
          {segments.map((s) => (
            <button key={s.etat || 'toutes'} type="button" role="radio" aria-checked={etatValidation === s.etat} onClick={() => changer('etat', s.etat)}>
              {s.libelle}
            </button>
          ))}
        </div>
      </form>

      {requete.isLoading && <Squelette lignes={8} />}
      {requete.error && <MessageErreur erreur={requete.error} />}
      {requete.data && (
        <div className="deux-colonnes">
          <section className="principal" aria-label="Liste des installations">
            <div className="tableau-conteneur">
              <table className="tableau lignes-48">
                <caption className="visuellement-cache">Installations</caption>
                <thead>
                  <tr>
                    <th scope="col">Nom</th>
                    <th scope="col" className="masquable">
                      Catégorie
                    </th>
                    <th scope="col" className="masquable">
                      Province
                    </th>
                    <th scope="col">État</th>
                    <th scope="col" className="masquable">
                      Confidentialité
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.length === 0 && (
                    <tr>
                      <td colSpan={5} className="attenue">
                        Aucune installation ne correspond à ces critères dans votre périmètre.
                      </td>
                    </tr>
                  )}
                  {lignes.map((i) => (
                    <tr key={i.id} aria-selected={selection?.id === i.id}>
                      <td>
                        <button type="button" className="bouton-ligne" aria-pressed={selection?.id === i.id} onClick={() => { setChoisie(i.id); montrerSurPetitEcran('titre-resume'); }}>
                          {i.nom}
                          <small>{i.organisationNom}</small>
                        </button>
                      </td>
                      <td className="masquable">{libelleCategorie(i.categorie)}</td>
                      <td className="masquable">
                        {libelleProvince(i.provinceCode)}
                        <small>{i.departementNom ?? i.localiteNom ?? 'non rattachée'}</small>
                      </td>
                      <td>
                        <span className={classeEtat(i.etatValidation)}>{libelleEtat(i.etatValidation)}</span>
                      </td>
                      <td className="masquable attenue">{libelleConfidentialite(i.confidentialite)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="tableau-pied">
                <span>
                  {formaterNombre(lignes.length)} installation{lignes.length > 1 ? 's' : ''} affichée{lignes.length > 1 ? 's' : ''}
                  {total !== null && !q ? ` sur ${formaterNombre(total)}` : ''}
                </span>
                {requete.hasNextPage && (
                  <button type="button" className="bouton" disabled={requete.isFetchingNextPage} onClick={() => void requete.fetchNextPage()}>
                    Afficher la suite
                  </button>
                )}
              </div>
            </div>
            {categorie === 'station' && (
              <p className="attenue espace-haut">Autonomie et dernière déclaration de chaque station : indisponibles tant qu’aucune déclaration de stock n’est reçue (circuit des déclarations non encore en service).</p>
            )}
          </section>
          {selection && (
            <aside className="lateral" aria-labelledby="titre-resume">
              <section className="carte-bloc resume-installation">
                <span className={classeEtat(selection.etatValidation)}>{libelleEtat(selection.etatValidation)}</span>
                <h2 id="titre-resume">{selection.nom}</h2>
                <dl className="paires">
                  <div>
                    <dt>Catégorie</dt>
                    <dd>{libelleCategorie(selection.categorie)}</dd>
                  </div>
                  <div>
                    <dt>Organisation</dt>
                    <dd>{selection.organisationNom}</dd>
                  </div>
                  <div>
                    <dt>Province</dt>
                    <dd>
                      {libelleProvince(selection.provinceCode)}
                      {selection.localiteNom ? `, ${selection.localiteNom}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt>Confidentialité</dt>
                    <dd>{libelleConfidentialite(selection.confidentialite)}</dd>
                  </div>
                </dl>
                {selection.etatValidation === 'en_attente_validation' && (
                  <p className="message avertissement">{administrateur ? 'À examiner : la validation ou le rejet motivé se fait depuis la fiche.' : 'La validation relève de l’administrateur national.'}</p>
                )}
                {selection.etatValidation === 'brouillon' && <p className="message avertissement">Brouillon : à compléter puis soumettre à validation depuis la fiche.</p>}
                <div className="actions colonne">
                  <Link className="bouton primaire" to={`/referentiel/installations/${selection.id}`}>
                    Ouvrir la fiche
                  </Link>
                  <Link className="bouton" to={`/documents?objetId=${selection.id}`}>
                    Voir les documents
                  </Link>
                </div>
              </section>
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
